import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Pagination } from 'enums';
import { useAppSelector, useTypedDispatch } from 'hooks';
import {
  CommunityType,
  fetchCommunities,
  selectCommunities,
  selectCommunitiesPagination,
  UserType,
} from 'state';
import { searchCommunities, setCommunitiesPagination } from 'state/actions';
import { ISelectOption } from 'ui-kit/Select/Select';
import { getOptions } from 'utils';

export type SelectCommunitiesType = {
  communitiesData: CommunityType[];
  communitiesIds: string;
  selectOptions: ISelectOption[];
  selectedValue: ISelectOption[];
  setSelectedValue: React.Dispatch<React.SetStateAction<ISelectOption[]>>;
  onChangeSelect: (communities: any) => void;
  onDeleteAllClick: () => void;
  onDeleteIconClick: (id: string) => void;
  onFocusSelect: () => void;
  onInputChangeSelect: (value?: string) => void;
  onMenuScrollToBottom: (e?: any) => void;
};

type UseSelectCommunitiesType = {
  getOptions?: boolean;
  user?: UserType;
};

export const useSelectCommunities = ({
  getOptions: getter = false,
  user,
}: UseSelectCommunitiesType): SelectCommunitiesType => {
  const [selectedValue, setSelectedValue] = useState<ISelectOption[]>(
    user ? getOptions(user?.communities, 'id', 'name') : [],
  );
  const dispatch = useTypedDispatch();
  const communities = useAppSelector(selectCommunities);
  const { page, count } = useAppSelector(selectCommunitiesPagination);

  const communitiesData = useMemo(() => {
    return (
      communities?.filter(community =>
        selectedValue?.map(item => item.value).includes(community.id),
      ) ?? []
    );
  }, [communities, selectedValue]);

  const communitiesIds = useMemo(() => {
    return selectedValue?.map(item => item.value).join();
  }, [selectedValue]);

  const selectOptions = useMemo(() => {
    return user
      ? getOptions(communities, 'id', 'name').filter(
          community => !user.communities?.map(({ id }) => id).includes(community.value),
        )
      : getOptions(communities, 'id', 'name');
  }, [communities, user]);

  const onChangeSelect = (communities: any) => {
    setSelectedValue(communities);
  };

  const onInputChangeSelect = useCallback(
    (value?: string) => {
      dispatch(searchCommunities(value || ''));
      dispatch(fetchCommunities(false));
    },
    [dispatch],
  );

  const onFocusSelect = () => {
    dispatch(fetchCommunities(false));
  };

  const onDeleteIconClick = (id: string) => {
    setSelectedValue(selectedValue?.filter(community => community.value !== id) ?? []);
  };

  const onDeleteAllClick = () => {
    setSelectedValue([]);
  };

  const onMenuScrollToBottom = (e?: any) => {
    if (e) e.stopPropagation();
    if (count && communities.length < count && page * Pagination.TAKE < count) {
      dispatch(setCommunitiesPagination({ page: page + 1 }));
      dispatch(fetchCommunities(true));
    }
  };

  useEffect(() => {
    if (getter) dispatch(fetchCommunities(false));
  }, [dispatch, getter]);

  return {
    communitiesData,
    communitiesIds,
    selectOptions,
    onFocusSelect,
    onChangeSelect,
    onInputChangeSelect,
    onDeleteIconClick,
    selectedValue,
    setSelectedValue,
    onDeleteAllClick,
    onMenuScrollToBottom,
  };
};
